import { useMemo, useState } from "react";
import { CheckCheck, Info, Loader2Icon } from "lucide-react";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BacklogTable } from "@/components/requestPlanning/BacklogTable";
import { MarkWafersAsMeasuredDialog } from "@/components/requestPlanning/MarkWafersAsMeasuredDialog";
import { useMeasurementSystems } from "@/hooks/useMeasurementSystems";
import {
  isPlanningKey,
  useMarkWaferRequestAsMeasured,
  usePlanning,
} from "@/hooks/useRequestPlanning";
import { LoadingDisplay } from "@/components/shared/LoadingDisplay";
import { GenericErrorDisplay } from "@/components/shared/ErrorDisplay";
import { formatMinutesToDuration } from "@/utils/timeFormatting";
import { MeasurementRequestsPlanningTable } from "@/components/requestPlanning/MeasurementRequestsPlanningTable";
import { Card, CardContent } from "@/components/ui/card";
import Tooltip from "@/components/shared/Tooltip";
import { Main } from "@/components/shared/Main";
import { Combobox } from "@/components/ui/combobox";
import {
  groupPlanningRequests,
  planningStatusesByTab,
  PlanningTab,
} from "../utils/requestPlanning";

export function RequestPlanningPage() {
  const [selectedMeasurementSystem, setSelectedMeasurementSystem] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<PlanningTab>("PLANNING");
  const [selectedPlannedWafers, setSelectedPlannedWafers] = useState<Set<string>>(() => new Set());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { mutate } = useSWRConfig();

  const { trigger: markAsMeasured, isMutating } = useMarkWaferRequestAsMeasured();

  const {
    data: measurementSystems,
    isLoading: isMeasurementSystemsLoading,
    error: isMeasurementSystemsError,
  } = useMeasurementSystems();
  const {
    data: measurementRequests,
    isLoading: isMeasurementRequestsLoading,
    error: isMeasurementRequestsError,
  } = usePlanning(planningStatusesByTab[activeTab]);

  const MEASUREMENT_SYSTEMS = useMemo(() => {
    return [{ id: 0, name: "All", capabilities: [] }, ...measurementSystems];
  }, [measurementSystems]);

  const handleMarkingAsCompleted = async (
    waferTimes: Record<string, { startedAt: string; completedAt: string }>,
  ) => {
    try {
      if (selectedPlannedWafers.size <= 0) {
        return;
      }

      const wafers = Array.from(selectedPlannedWafers);

      const requestsToPlan = wafers.map((key) => {
        const [reqId, waferId] = key.split("-");
        const waferTime = waferTimes[key];
        return {
          measurement_request_id: Number(reqId),
          measurement_system_id: 0,
          wafer_id: Number(waferId),
          started_at: waferTime?.startedAt,
          completed_at: waferTime?.completedAt,
        };
      });

      await markAsMeasured(
        { requests_to_plan: requestsToPlan },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: () => {
            toast.success("Successfully marked the wafers as Measured!");
            mutate(isPlanningKey);
            setSelectedPlannedWafers(new Set());
            setIsDialogOpen(false);
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenDialog = () => {
    if (selectedPlannedWafers.size <= 0) {
      toast.error("Please select wafers to mark as measured");
      return;
    }
    setIsDialogOpen(true);
  };
  const requestsByStatus = useMemo(
    () => groupPlanningRequests(measurementRequests, selectedMeasurementSystem),
    [measurementRequests, selectedMeasurementSystem],
  );

  const wafersCountPerMeasurementSystem = useMemo(() => {
    if (measurementSystems.length === 0) {
      return {};
    }

    const initialState = measurementSystems.reduce(
      (acc, cur) => {
        const plannedKey = `scheduled-${cur.id}`;
        const completedKey = `measured-${cur.id}`;

        acc[plannedKey] = { count: 0, totalTime: 0 };
        acc[completedKey] = { count: 0, totalTime: 0 };

        return acc;
      },
      {} as Record<string, { count: number; totalTime: number }>,
    );

    return measurementRequests.reduce((acc, cur) => {
      cur.waferRequests.forEach((wafer) => {
        if (wafer.status === "TO_SCHEDULE") return;

        const totalWaferTime = wafer.estimatedTime * wafer.dieCount;
        const measurementSystemId = wafer.measurementSystem?.id;

        if (measurementSystemId === undefined || measurementSystemId === null) {
          return;
        }

        if (wafer.status === "SCHEDULED") {
          acc[`scheduled-${measurementSystemId}`].count += 1;
          acc[`scheduled-${measurementSystemId}`].totalTime += totalWaferTime;
        }

        if (
          wafer.status === "MEASURING" ||
          wafer.status === "MEASURED" ||
          wafer.status === "MEASURING_FAILED" ||
          wafer.status === "ANALYSING" ||
          wafer.status === "ANALYSED" ||
          wafer.status === "ANALYSIS_FAILED"
        ) {
          acc[`measured-${measurementSystemId}`].count += 1;
          acc[`measured-${measurementSystemId}`].totalTime += totalWaferTime;
        }
      });

      return acc;
    }, initialState);
  }, [measurementRequests, measurementSystems]);

  // Calculate wafer progress for each measurement request
  const waferProgressPerRequest = useMemo(() => {
    const progress = new Map<number, { backlog: number; scheduled: number; measured: number }>();

    measurementRequests.forEach((request) => {
      if (request.waferProgress) {
        progress.set(request.id, request.waferProgress);
        return;
      }
      let backlogCount = 0;
      let scheduledCount = 0;
      let measuredCount = 0;

      request.waferRequests.forEach((wafer) => {
        switch (wafer.status) {
          case "TO_SCHEDULE":
            backlogCount++;
            break;
          case "MEASURING":
          case "MEASURED":
          case "ANALYSING":
          case "ANALYSED":
          case "MEASURING_FAILED":
          case "ANALYSIS_FAILED":
            measuredCount++;
            break;
          default:
            scheduledCount++;
            break;
        }
      });

      progress.set(request.id, {
        backlog: backlogCount,
        scheduled: scheduledCount,
        measured: measuredCount,
      });
    });

    return progress;
  }, [measurementRequests]);

  const selectedSystemWaferCount = useMemo(() => {
    if (activeTab !== "PLANNING" || !selectedMeasurementSystem) {
      return null;
    }

    const key = `scheduled-${selectedMeasurementSystem}`;
    const data = wafersCountPerMeasurementSystem[key];

    return (
      <Tooltip
        asChild
        content={
          <>
            <span>Scheduled wafers: {data?.count ?? 0}</span>
            <br />
            <span>Total time: {formatMinutesToDuration(data?.totalTime ?? 0)}</span>
          </>
        }
        trigger={
          <Button variant="ghost" size="icon" type="button">
            <Info />
          </Button>
        }
        side="top"
      />
    );
  }, [activeTab, selectedMeasurementSystem, wafersCountPerMeasurementSystem]);

  if (isMeasurementRequestsLoading || isMeasurementSystemsLoading) {
    return <LoadingDisplay />;
  }

  if (isMeasurementRequestsError || isMeasurementSystemsError) {
    return <GenericErrorDisplay />;
  }

  return (
    <Main className="space-y-6 mx-auto w-full max-w-screen-2xl" fluid>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Request Planning</h1>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Tabs
            value={activeTab}
            onValueChange={(value) => {
              setActiveTab(value as PlanningTab);
              setSelectedPlannedWafers(new Set());
            }}
          >
            <TabsList>
              <TabsTrigger value="PLANNING">Planning</TabsTrigger>
              <TabsTrigger value="PROCESSING">Processing</TabsTrigger>
              <TabsTrigger value="HISTORY">History</TabsTrigger>
            </TabsList>
          </Tabs>
          {activeTab === "PLANNING" && (
            <Button
              onClick={handleOpenDialog}
              disabled={selectedPlannedWafers.size === 0 || isMutating}
            >
              {isMutating ? (
                <Loader2Icon className="animate-spin" />
              ) : (
                <CheckCheck className="h-4 w-4" />
              )}
              Mark{" "}
              {selectedPlannedWafers.size > 0 &&
                `${selectedPlannedWafers.size} wafer${selectedPlannedWafers.size > 1 ? "s" : ""}`}{" "}
              as measured
            </Button>
          )}
        </div>
        <>
          <div className="flex items-center gap-2">
            <Combobox
              className="w-80"
              value={selectedMeasurementSystem.toString()}
              onChange={(value) => {
                setSelectedMeasurementSystem(Number(value));
                setSelectedPlannedWafers(new Set());
              }}
              placeholder="Select measurement system"
              searchPlaceholder="Search measurement systems..."
              options={MEASUREMENT_SYSTEMS.map((system) => ({
                key: system.id.toString(),
                value: system.id.toString(),
                label: system.name,
              }))}
            />
            {selectedSystemWaferCount}
          </div>
          <Card>
            <CardContent>
              <MeasurementRequestsPlanningTable
                key={`${activeTab}-${selectedMeasurementSystem}`}
                measurementSystem={
                  MEASUREMENT_SYSTEMS.find((s) => s.id === selectedMeasurementSystem)!.name
                }
                onWaferSelectionChange={setSelectedPlannedWafers}
                rows={requestsByStatus[activeTab.toLowerCase() as keyof typeof requestsByStatus]}
                selectedWafers={selectedPlannedWafers}
                showCheckboxes={activeTab === "PLANNING"}
                activeTab={activeTab}
                waferProgressPerRequest={waferProgressPerRequest}
              />
            </CardContent>
          </Card>
        </>
      </div>

      {activeTab === "PLANNING" && (
        <BacklogTable
          measurementSystems={measurementSystems}
          rows={requestsByStatus.backlog}
          waferProgressPerRequest={waferProgressPerRequest}
        />
      )}

      {/* Mark Wafers as Measured Dialog */}
      <MarkWafersAsMeasuredDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        selectedWaferKeys={selectedPlannedWafers}
        measurementRequests={measurementRequests}
        onConfirm={handleMarkingAsCompleted}
        isLoading={isMutating}
      />
    </Main>
  );
}
